import { Link } from "react-router-dom";
import type { ArticleItem } from "@/mocks/ranking";

export function rankColor(rank: number) {
  if (rank === 1) return "bg-accent-500 text-foreground-950";
  if (rank === 2) return "bg-secondary-400 text-background-50";
  if (rank === 3) return "bg-secondary-600 text-background-50";
  return "bg-background-200 text-foreground-600";
}

export function RankBadge({ rank }: { rank: number }) {
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-display text-base ${rankColor(rank)}`}
      aria-label={`第 ${rank} 名`}
    >
      {rank}
    </span>
  );
}

export function Tag({ label }: { label: string }) {
  return (
    <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-700">
      {label}
    </span>
  );
}

export function ArticleCard({ article, showRank = false }: { article: ArticleItem; showRank?: boolean }) {
  return (
    <Link
      to={`/rank/${article.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg bg-background-50 ring-1 ring-background-200/70 transition-all hover:-translate-y-1 hover:ring-primary-300"
    >
      <div className="relative aspect-[3/2] overflow-hidden">
        <img
          src={article.cover}
          alt={`${article.title}封面配图`}
          title={`${article.title} · 吃瓜榜单`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {showRank && (
          <span className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-foreground-950/80 font-display text-base text-accent-400 backdrop-blur">
            {article.rank}
          </span>
        )}
        <span className="absolute right-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-medium text-primary-700">
          {article.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="line-clamp-2 text-sm font-bold leading-6 text-foreground-950 group-hover:text-primary-700 md:text-base">
          {article.title}
        </h3>
        <p className="line-clamp-2 text-xs leading-5 text-foreground-600 md:text-sm">{article.excerpt}</p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-2">
          <span className="text-xs text-foreground-500">{article.month} · {article.date}</span>
          <span className="flex items-center gap-1 text-xs text-primary-600">
            <i className="ri-fire-line" aria-hidden="true"></i>
            {article.heat}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  desc,
  to,
  linkLabel,
}: {
  eyebrow: string;
  title: string;
  desc?: string;
  to?: string;
  linkLabel?: string;
}) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
          <span className="inline-block h-2 w-2 rounded-full bg-primary-500" aria-hidden="true"></span>
          {eyebrow}
        </p>
        <h2 className="text-2xl font-black text-foreground-950 md:text-3xl">{title}</h2>
        {desc && <p className="mt-2 max-w-2xl text-sm text-foreground-600">{desc}</p>}
      </div>
      {to && (
        <Link
          to={to}
          className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-4 py-2 text-sm font-medium text-primary-700 ring-1 ring-background-200 hover:bg-primary-100 transition-colors"
        >
          {linkLabel || "查看全部"}
          <i className="ri-arrow-right-line" aria-hidden="true"></i>
        </Link>
      )}
    </div>
  );
}

export function BackToTop() {
  return (
    <button
      type="button"
      aria-label="回到顶部"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-none hover:bg-primary-600 transition-colors"
    >
      <i className="ri-arrow-up-line text-xl" aria-hidden="true"></i>
    </button>
  );
}